import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useClickUpSpaces, useClientAudit, useClientStructure } from 'api/aws/strategy-builder/useStrategyBuilder'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AutoScrollBox } from 'styles/elements/styleElements'

// ── Constants ────────────────────────────────────────────────────────────────

const STANDARD_LISTS = [
  'SEO',
  'PPC',
  'ANA',
  'CREATIVE',
  'WEB',
  'Client Details',
  'Client Success',
  'Client Messages',
  'Onboarding',
] as const

// ── Scrollable Column ────────────────────────────────────────────────────────

interface ColumnItem {
  id: string
  name: string
}

const BrowserColumn = ({
  title,
  items,
  selectedIds,
  onToggle,
  loading,
  searchPlaceholder = 'Search...',
  count,
  showSelectControls,
  onSelectRange,
  onSelectAll,
  onClear,
  emptyMessage = 'No items',
}: {
  title: string
  items: ColumnItem[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  loading?: boolean
  searchPlaceholder?: string
  count?: number
  showSelectControls?: boolean
  onSelectRange?: (n: number) => void
  onSelectAll?: () => void
  onClear?: () => void
  emptyMessage?: string
}) => {
  const theme = useTheme()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return items
    const q = search.toLowerCase()
    return items.filter(i => i.name.toLowerCase().includes(q))
  }, [items, search])

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 200 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {title}
          </Typography>
          {count != null && (
            <Typography variant="caption" color="text.secondary">
              ({count})
            </Typography>
          )}
          {selectedIds.size > 0 && (
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {selectedIds.size}
            </Typography>
          )}
        </Box>
        {showSelectControls && items.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {onSelectRange && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Select:
                </Typography>
                {[5, 10, 25].map(n => (
                  <Typography
                    key={n}
                    variant="caption"
                    color="primary.main"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => onSelectRange(n)}
                  >
                    {n}
                  </Typography>
                ))}
              </>
            )}
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={onSelectAll}
            >
              All
            </Typography>
            <Typography variant="caption" color="text.disabled">
              |
            </Typography>
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={onClear}
            >
              Clear
            </Typography>
          </Box>
        )}
      </Box>

      {/* Search */}
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={search}
        onChange={e => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 0.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* List */}
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 350,
          minHeight: 180,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography variant="caption" color="text.disabled">
              {items.length === 0 ? emptyMessage : 'No matches'}
            </Typography>
          </Box>
        ) : (
          filtered.map(item => (
            <Box
              key={item.id}
              onClick={() => onToggle(item.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.5,
                cursor: 'pointer',
                bgcolor: selectedIds.has(item.id) ? `${theme.palette.primary.main}15` : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Checkbox
                size="small"
                checked={selectedIds.has(item.id)}
                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {item.name}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

const ClientAuditPage = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const spacesQuery = useClickUpSpaces()

  useEffect(() => {
    if (!spacesQuery.data && !spacesQuery.isLoading) spacesQuery.refetch()
  }, [])

  // Selection state
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set())
  const [foldersLoaded, setFoldersLoaded] = useState(false)
  const [listsLoaded, setListsLoaded] = useState(false)

  // Queries
  const structureQuery = useClientStructure(foldersLoaded ? selectedSpaceId : null)
  const auditQuery = useClientAudit(listsLoaded ? selectedFolderId : null)
  const spaces = spacesQuery.data ?? []
  const folders: ColumnItem[] = useMemo(() => {
    return (structureQuery.data?.clients ?? []).map((f: any) => ({
      id: f.folderId,
      name: f.folderName || f.prefix || 'Unknown',
    }))
  }, [structureQuery.data])

  const lists: ColumnItem[] = useMemo(() => {
    if (!auditQuery.data?.lists) return []
    return auditQuery.data.lists.map((l: any) => ({
      id: l.listId || l.id || '',
      name: l.listName || l.name || 'Unknown',
    }))
  }, [auditQuery.data])

  // Standard list matching
  const matchedStandard = useMemo(() => {
    if (!auditQuery.data) return new Map()
    const matched = new Map<string, { listName: string; taskCount: number }>()
    for (const l of auditQuery.data.lists ?? []) {
      const name = l.listName || l.name || ''
      for (const std of STANDARD_LISTS) {
        if (name.toLowerCase().includes(std.toLowerCase())) {
          matched.set(std, { listName: name, taskCount: l.taskCount ?? 0 })
        }
      }
    }
    return matched
  }, [auditQuery.data])

  // Handlers
  const handleSelectSpace = useCallback((id: string) => {
    setSelectedSpaceId(prev => (prev === id ? null : id))
    setSelectedFolderId(null)
    setSelectedListIds(new Set())
    setFoldersLoaded(false)
    setListsLoaded(false)
  }, [])

  const handleSelectFolder = useCallback((id: string) => {
    setSelectedFolderId(prev => (prev === id ? null : id))
    setSelectedListIds(new Set())
    setListsLoaded(false)
  }, [])

  const handleSelectList = useCallback((id: string) => {
    setSelectedListIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleLoadFolders = useCallback(() => {
    setFoldersLoaded(true)
  }, [])
  const handleLoadLists = useCallback(() => {
    setListsLoaded(true)
  }, [])

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
  }, [])

  const spaceSelectedIds = useMemo(() => (selectedSpaceId ? new Set([selectedSpaceId]) : new Set<string>()), [
    selectedSpaceId,
  ])
  const folderSelectedIds = useMemo(() => (selectedFolderId ? new Set([selectedFolderId]) : new Set<string>()), [
    selectedFolderId,
  ])

  const completeness = STANDARD_LISTS.length > 0 ? Math.round((matchedStandard.size / STANDARD_LISTS.length) * 100) : 0

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        p: 3,
        paddingTop: pinned ? '60px' : 0,
        transition: 'padding-top 0.3s ease',
      }}
    >
      <AutoScrollBox>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <FactCheckIcon sx={{ fontSize: 28, color: 'rgb(66, 165, 245)' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Client Structure Audit
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Browse and audit client folder structures against the standard template.
        </Typography>

        {/* Browser Panel */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            Select a list to view its structure
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Column 1: Spaces */}
            <BrowserColumn
              title="Spaces"
              items={spaces.map((s: any) => ({ id: s.id, name: s.name }))}
              selectedIds={spaceSelectedIds}
              onToggle={handleSelectSpace}
              loading={spacesQuery.isLoading}
              searchPlaceholder="Search..."
              count={spaces.length}
            />

            {/* Column 2: Folders */}
            {selectedSpaceId && !foldersLoaded ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
                >
                  Folders
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 180,
                    gap: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    1 space selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleLoadFolders}
                    sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}
                  >
                    Load Folders
                  </Button>
                </Box>
              </Box>
            ) : (
              <BrowserColumn
                title="Folders"
                items={folders}
                selectedIds={folderSelectedIds}
                onToggle={handleSelectFolder}
                loading={structureQuery.isLoading}
                searchPlaceholder="Search..."
                count={folders.length}
                showSelectControls
                onSelectRange={n => {
                  const subset = folders.slice(0, n).map(f => f.id)
                  setSelectedFolderId(subset[0] || null)
                }}
                onSelectAll={() => setSelectedFolderId(folders[0]?.id || null)}
                onClear={() => {
                  setSelectedFolderId(null)
                  setListsLoaded(false)
                }}
                emptyMessage={selectedSpaceId ? 'No folders' : 'Select a space'}
              />
            )}

            {/* Column 3: Lists */}
            {selectedFolderId && !listsLoaded ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
                >
                  Lists
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 180,
                    gap: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    1 folder selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleLoadLists}
                    sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}
                  >
                    Load Lists
                  </Button>
                </Box>
              </Box>
            ) : (
              <BrowserColumn
                title="Lists"
                items={lists}
                selectedIds={selectedListIds}
                onToggle={handleSelectList}
                loading={auditQuery.isLoading}
                searchPlaceholder="Search..."
                count={lists.length}
                showSelectControls
                onSelectAll={() => setSelectedListIds(new Set(lists.map(l => l.id)))}
                onClear={() => setSelectedListIds(new Set())}
                emptyMessage={selectedFolderId ? 'No lists' : 'Select a folder'}
              />
            )}
          </Box>

          {/* Selected IDs */}
          {(selectedSpaceId || selectedFolderId || selectedListIds.size > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Selected IDs
                </Typography>
                {selectedSpaceId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Space:
                    </Typography>
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                      {selectedSpaceId}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(selectedSpaceId)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {selectedFolderId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Folder:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {selectedFolderId}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(selectedFolderId)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {selectedListIds.size > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      List:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {Array.from(selectedListIds)[0]}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(Array.from(selectedListIds)[0])}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Audit Detail Below */}
        {listsLoaded &&
          auditQuery.data &&
          (() => {
            const auditLists = auditQuery.data.lists ?? []
            const totalTasks = auditLists.reduce((s: number, l: any) => s + (l.taskCount ?? 0), 0)
            const folderName = folders.find(f => f.id === selectedFolderId)?.name || 'Folder'
            const presentCount = matchedStandard.size
            const missingCount = STANDARD_LISTS.length - presentCount

            return (
              <Paper
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Header bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    bgcolor:
                      completeness === 100 ? `${theme.palette.success.main}10` : `${theme.palette.warning.main}10`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {completeness === 100 ? (
                      <CheckBoxIcon sx={{ fontSize: 22, color: 'success.main' }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon sx={{ fontSize: 22, color: 'warning.main' }} />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {folderName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor:
                          completeness === 100 ? `${theme.palette.success.main}20` : `${theme.palette.warning.main}20`,
                        color: completeness === 100 ? 'success.main' : 'warning.main',
                      }}
                    >
                      {completeness}% coverage
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {auditLists.length} lists · {totalTasks} tasks
                  </Typography>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {/* Standard Lists — Grid layout */}
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 1, display: 'block', mb: 1.5 }}
                  >
                    Standard Lists
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 1,
                      mb: 3,
                    }}
                  >
                    {STANDARD_LISTS.map(std => {
                      const match = matchedStandard.get(std)
                      const present = !!match
                      return (
                        <Box
                          key={std}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            border: `1px solid ${present ? theme.palette.success.main + '40' : theme.palette.divider}`,
                            bgcolor: present ? `${theme.palette.success.main}08` : 'transparent',
                          }}
                        >
                          {present ? (
                            <CheckBoxIcon sx={{ fontSize: 20, color: 'success.main' }} />
                          ) : (
                            <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: present ? 'text.primary' : 'text.disabled',
                                fontSize: '0.8rem',
                              }}
                            >
                              {std}
                            </Typography>
                            {match && (
                              <Typography variant="caption" color="text.secondary">
                                {match.taskCount} task{match.taskCount !== 1 ? 's' : ''}
                              </Typography>
                            )}
                            {!present && (
                              <Typography variant="caption" color="text.disabled">
                                Not found
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>

                  {/* Custom Fields — Grid layout */}
                  {auditQuery.data.customFieldAudit && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: 1, display: 'block', mb: 1.5 }}
                      >
                        Key Custom Fields
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {(auditQuery.data.customFieldAudit.presentKeyFields ?? []).map((f: string) => (
                          <Box
                            key={f}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.success.main}40`,
                              bgcolor: `${theme.palette.success.main}08`,
                            }}
                          >
                            <CheckBoxIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {f}
                            </Typography>
                          </Box>
                        ))}
                        {(auditQuery.data.customFieldAudit.missingKeyFields ?? []).map((f: string) => (
                          <Box
                            key={f}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                              {f}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            )
          })()}

        {/* Custom Fields Detail — shown when a list is selected */}
        {selectedListIds.size > 0 && auditQuery.data && (
          <ListCustomFieldsDetail lists={auditQuery.data.lists ?? []} selectedListIds={selectedListIds} />
        )}
      </AutoScrollBox>
    </Box>
  )
}

// ── Custom Fields Detail for Selected List ──────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  drop_down: '#ab47bc',
  labels: '#ef5350',
  short_text: '#42a5f5',
  text: '#42a5f5',
  number: '#66bb6a',
  currency: '#66bb6a',
  date: '#ff9800',
  email: '#26c6da',
  url: '#26c6da',
  checkbox: '#78909c',
  formula: '#5c6bc0',
  automatic_progress: '#7e57c2',
  users: '#ec407a',
  attachment: '#8d6e63',
}

const ListCustomFieldsDetail = ({ lists, selectedListIds }: { lists: any[]; selectedListIds: Set<string> }) => {
  const theme = useTheme()
  const [fieldSearch, setFieldSearch] = useState('')
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const selectedList = useMemo(() => {
    const firstId = Array.from(selectedListIds)[0]
    return lists.find((l: any) => (l.listId || l.id) === firstId)
  }, [lists, selectedListIds])

  const fields = useMemo(() => {
    const raw = selectedList?.customFields ?? []
    if (!fieldSearch.trim()) return raw
    const q = fieldSearch.toLowerCase()
    return raw.filter((f: any) => (f.name || '').toLowerCase().includes(q) || (f.type || '').toLowerCase().includes(q))
  }, [selectedList, fieldSearch])

  // Group fields by type
  const groupedFields = useMemo(() => {
    const groups: Record<string, any[]> = {}
    for (const f of fields) {
      const type = f.type || 'unknown'
      if (!groups[type]) groups[type] = []
      groups[type].push(f)
    }
    // Sort groups by count (most fields first)
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length)
  }, [fields])

  // Type summary for header chips
  const typeSummary = useMemo(() => {
    return groupedFields.map(([type, items]) => ({ type, count: items.length, color: TYPE_COLORS[type] || '#78909c' }))
  }, [groupedFields])

  const toggleExpand = (fieldId: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev)
      next.has(fieldId) ? next.delete(fieldId) : next.add(fieldId)
      return next
    })
  }

  const toggleGroup = (type: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      next.has(type) ? next.delete(type) : next.add(type)
      return next
    })
  }

  if (!selectedList || !selectedList.customFields?.length) {
    return (
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 3, textAlign: 'center' }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedListIds.size > 0
            ? 'No custom fields found for this list'
            : 'Select a list to view its custom fields'}
        </Typography>
      </Paper>
    )
  }

  const listName = selectedList.listName || selectedList.name || 'Selected List'

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* Header with search + type summary */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: `${theme.palette.primary.main}08`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            Custom Fields — {listName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedList.customFields.length} fields
          </Typography>
        </Box>
        <TextField
          placeholder="Search fields..."
          value={fieldSearch}
          onChange={e => setFieldSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', bgcolor: 'background.default' } }}
        />
        {/* Type summary chips */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {typeSummary.map(({ type, count, color }) => (
            <Chip
              key={type}
              label={`${type} (${count})`}
              size="small"
              onClick={() => toggleGroup(type)}
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontWeight: 600,
                bgcolor: collapsedGroups.has(type) ? 'transparent' : `${color}20`,
                color: collapsedGroups.has(type) ? 'text.disabled' : color,
                border: `1px solid ${collapsedGroups.has(type) ? theme.palette.divider : color}40`,
                cursor: 'pointer',
                textDecoration: collapsedGroups.has(type) ? 'line-through' : 'none',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Grouped field list */}
      <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
        {groupedFields.map(([type, typeFields]) => {
          const typeColor = TYPE_COLORS[type] || '#78909c'
          const isCollapsed = collapsedGroups.has(type)

          return (
            <Box key={type}>
              {/* Group header */}
              <Box
                onClick={() => toggleGroup(type)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  bgcolor: `${typeColor}08`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: `${typeColor}15` },
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    color: typeColor,
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
                <Box sx={{ width: 4, height: 12, borderRadius: 0.5, bgcolor: typeColor }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: typeColor,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.65rem',
                  }}
                >
                  {type}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                  ({typeFields.length})
                </Typography>
              </Box>

              {/* Fields in group — compact 2-column grid */}
              <Collapse in={!isCollapsed}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {typeFields.map((field: any, idx: number) => {
                    const hasOptions = field.type_config?.options?.length > 0
                    const isExpanded = expandedFields.has(field.id)
                    const optionCount = field.type_config?.options?.length ?? 0

                    return (
                      <Box
                        key={field.id || idx}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderRight: idx % 2 === 0 ? `1px solid ${theme.palette.divider}` : 'none',
                          borderBottom: `1px solid ${theme.palette.divider}05`,
                          '&:hover': { bgcolor: 'action.hover' },
                          gridColumn: isExpanded ? '1 / -1' : undefined,
                        }}
                      >
                        {/* Field name + actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }} noWrap>
                            {field.name}
                          </Typography>
                          {hasOptions && (
                            <Typography
                              variant="caption"
                              onClick={() => toggleExpand(field.id)}
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                flexShrink: 0,
                                ml: 0.5,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {isExpanded ? 'Hide' : optionCount}
                            </Typography>
                          )}
                        </Box>
                        {/* ID row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.disabled' }}
                            noWrap
                          >
                            {field.id}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(field.id)}
                            sx={{ p: 0.15 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 10, color: 'text.disabled' }} />
                          </IconButton>
                        </Box>
                        {/* Expandable options */}
                        {hasOptions && (
                          <Collapse in={isExpanded}>
                            <Box sx={{ pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {field.type_config.options.map((opt: any, oi: number) => (
                                <Box key={oi} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: opt.color || typeColor,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', flex: 1 }} noWrap>
                                    {opt.name || opt.label || `Option ${oi + 1}`}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.55rem', color: 'text.disabled' }}
                                    noWrap
                                  >
                                    {opt.id || ''}
                                  </Typography>
                                  {opt.id && (
                                    <IconButton
                                      size="small"
                                      onClick={() => navigator.clipboard.writeText(opt.id)}
                                      sx={{ p: 0.1 }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 9, color: 'text.disabled' }} />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Collapse>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}

export default ClientAuditPage
